import { useState, useMemo, useEffect } from "react"
import { getHeaders } from "./headers"
import { useTranslation } from "react-i18next"
import { Flex, Dropdown, Modal } from "antd"
import { Text } from "ui/Typography"
import GroupTags from "../GroupTags"
import { formatPhoneNumber } from "utils/data-formatters"
import { MoreOutlined } from "@ant-design/icons"
import styles from "./index.module.css"
import DataTable from "ui/DataTable"
import Avatar from "components/common/Avatar"
import BulkActions from "./BulkActions"
import PropTypes from "prop-types"
import ClientContactForm from "../ClientsContactWindow"
import { useNavigate } from "react-router-dom"
import { routes } from "router/routes"
import { bulkGuideAddGroup, CheckClientDelete, deleteClient, deleteGuideBulkGroups } from "store/clients"
import { useDispatch, useSelector } from "react-redux"
import CanNotDeleteClient from "./Modals/CanNotDeleteClient"
import DeleteClient from "./Modals/DeleteClient"
import { toaster } from "components/common/Toast"
import { createConversation } from "store/conversations"
import ClientGroupManage from "./Modals/ClientGroupManage"

const ClientTable = ({ clients, sortField, sortOrder, handleTableChange, handleFetchClients }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [isVisible, setIsVisible] = useState(false)
  const [contactType, setContactType] = useState("")
  const [userIds, setUserIds] = useState([])
  const [isGroupModalVisible, setIsGroupModalVisible] = useState(false)
  const [isCanNotDeleteClientVisible, setIsCanNotDeleteClientVisible] = useState(false)
  const [isDeleteClientVisible, setIsDeleteClientVisible] = useState(false)
  const [hideMenuList, setHideMenuList] = useState(false)
  const [selectedClient, setSelectedClient] = useState(null)
  const [groupAction, setGroupAction] = useState("")
  const groups = useSelector((state) => state.groups?.items || [])
  const isClientDeletable = useSelector((state) => state.clients.isClientDeletable)
  const isClientLoading = useSelector((state) => state.clients.loading)
  const isFailed = useSelector((state) => state.clients.failed)
  const [selectedClientIds, setSelectedClientIds] = useState([])
  const [selectedGroups, setSelectedGroups] = useState([])
  const [removeGroups, setRemoveGroups] = useState([])
  const [hasDeleteClient, setHasDeleteClient] = useState(false)
  const [clientId, setClientId] = useState(null)

  useEffect(() => {
    if (!hasDeleteClient || isClientLoading || !clientId || isFailed) {
      return
    }

    if (isClientDeletable) {
      setIsDeleteClientVisible(true)
    } else {
      setIsCanNotDeleteClientVisible(true)
    }

    // Reset states
    setHasDeleteClient(false)
  }, [hasDeleteClient, isClientLoading, isClientDeletable, clientId, isFailed])

  const handleDeleteClick = async () => {
    try {
      setIsDeleteClientVisible(false)
      setIsCanNotDeleteClientVisible(false)
      const response = await dispatch(deleteClient(clientId))
      toaster.success({
        title: "Client Details",
        text: response?.message || "Client deleted successfully"
      })
      handleFetchClients()
    } catch (error) {
      console.log(error);
      toaster.error({
        title: "Client Details",
        text: "Failed to delete client"
      })
    }
  }

  const handleGroupActionClick = () => {
    if (groupAction === "bulk_add" || groupAction === "add") {
      handleGroupSaveClick()
    } else if (groupAction === "bulk_remove" || groupAction === "remove") {
      handleRemoveGroup()
    }
  }

  const handleClose = () => {
    setIsVisible(false)
  }
  const title = "Send" + " " + t(`client.${contactType}`)

  const handleMenuClick = (type, client) => {
    setIsVisible(true)
    setContactType(type)
    setUserIds([client?.user_id])
  }

  const getMenuItems = (client) => [
    {
      key: "view",
      label: t("clients.menu_items.view"),
      disabled: true
    },
    {
      key: "edit",
      label: t("clients.menu_items.edit"),
      onClick: () => {
        navigate(routes.guideClientEditPath({ id: client.id }))
      },
      disabled: !client?.invited_by_guide
    },
    {
      key: "delete",
      label: t("clients.menu_items.delete"),
      onClick: () => {
        setHasDeleteClient(true)
        setClientId(client.id)
        dispatch(CheckClientDelete(client.id))
      }
    },
    {
      key: "add_to_group",
      label: t("clients.menu_items.add_to_group"),
      onClick: () => {
        setSelectedClient(client)
        setGroupAction("add")
        setIsGroupModalVisible(true)
        setSelectedClientIds([client.id])
      }
    },
    {
      key: "remove_from_group",
      label: t("clients.menu_items.remove_from_group"),
      onClick: () => {
        setSelectedClient(client)
        setGroupAction("remove")
        setIsGroupModalVisible(true)
        setHideMenuList(true)
        setSelectedClientIds([client.id])
      },
      disabled: !client.client_groups || client.client_groups.length === 0
    },
    {
      key: "email",
      label: t("clients.menu_items.email"),
      onClick: () => handleMenuClick("email", client)
    },
    {
      key: "message",
      label: t("clients.menu_items.message"),
      onClick: () => {
        dispatch(createConversation({ participant_id: client.user.id })).then((conversation) =>
          navigate(routes.guideMessagesPath({}, { conversation_id: conversation.id }))
        )
      }
    },
    {
      key: "text",
      label: t("clients.menu_items.text"),
      onClick: () => handleMenuClick("text", client),
      disabled: !client.user?.phone_number
    }
  ]

  const dataSource = useMemo(() => {
    return clients.map((client) => {
      const { total_booking_amount, last_trip_date, user, last_booked_trip_title, user_id } = client || {}
      const { phone_number, first_name, last_name, email } = user || {}
      return {
        key: user_id,
        name: (
          <Flex gap={16} align="center" className="ellipsis">
            <Avatar user={client.user} size={44} border={false} fit={false} />
            <Text>
              {first_name} {last_name}
            </Text>
          </Flex>
        ),
        group: (
          <GroupTags
            setIsGroupModalVisible={setIsGroupModalVisible}
            setSelectedGroups={setSelectedGroups}
            setGroupAction={setGroupAction}
            setSelectedClientIds={setSelectedClientIds}
            client={client}
          />
        ),
        email: email ? (
          <button type="button" className={`${styles.contactLink} ellipsis`} onClick={(e) => handleMenuClick("email", client)}>
            {email}
          </button>
        ) : null,
        phone: phone_number ? (
          <button type="button" className={`${styles.contactLink} ellipsis`} onClick={(e) => handleMenuClick("text", client)}>
            {formatPhoneNumber(phone_number)}
          </button>
        ) : null,
        last_booked_trip_title: last_booked_trip_title,
        experience_date: last_trip_date,
        ytd_spend: total_booking_amount,
        actions: (
          <Dropdown
            menu={{
              items: getMenuItems(client)
            }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <MoreOutlined className={styles.moreIcon} />
          </Dropdown>
        )
      }
    })
  }, [clients, t])

  const rowSelectionConfig = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys, selectedRows) => {
      setSelectedRowKeys(newSelectedRowKeys)
    }
  }

  const hasSelected = selectedRowKeys.length > 0

  const handleBulkGroupAction = (action, selectedKeys) => {
    const selectedClients = clients.filter((client) => selectedKeys.includes(client.user_id))

    const clientIds = selectedClients.map((client) => client.id)
    const clientGroups = selectedClients.flatMap((client) => client.client_groups || [])

    const uniqueGroupsMap = new Map()
      ;[...selectedGroups, ...clientGroups].forEach((group) => {
        if (group?.group_id && !uniqueGroupsMap.has(group.group_id)) {
          uniqueGroupsMap.set(group.group_id, group)
        }
      })

    setSelectedClientIds(clientIds)
    setSelectedGroups(Array.from(uniqueGroupsMap.values()))
    setGroupAction(action)
    setIsGroupModalVisible(true)
    setHideMenuList(action === "bulk_remove")
    setSelectedClient(null)
  }

  const onCanNotDeleteClientClose = () => {
    setIsCanNotDeleteClientVisible(false)
  }

  const onDeleteClientClose = () => {
    setIsDeleteClientVisible(false)
  }

  const handleViewBookingClick = () => {
    setIsCanNotDeleteClientVisible(false)
    navigate(routes.guideBookingsPath())
  }

  const handleGroupSaveClick = async () => {
    try {
      await dispatch(
        bulkGuideAddGroup({
          client_ids: selectedClientIds,
          group_ids: selectedGroups.map((group) => group.group_id),
          remove_group_ids: removeGroups.map((group) => group.group_id)
        })
      )
      setIsGroupModalVisible(false)
      setSelectedClient(null)
      setGroupAction("")
      setHideMenuList(false)
      setSelectedClientIds([])
      setRemoveGroups([])
      setSelectedGroups([])
      handleFetchClients()
    } catch (error) {
      console.error("Failed to bulk add client groups:", error)
    }
  }

  const handleSelectedGroupsChange = (groups) => {
    // Map for quick lookup by group_id
    const prevMap = new Map(selectedGroups.map((g) => [g.group_id, g]))
    const nextMap = new Map(groups.map((g) => [g.group_id, g]))
    const removeMap = new Map(removeGroups.map((g) => [g.group_id, g]))

    // Groups removed: in prevMap but not in nextMap
    const removed = Array.from(prevMap.values()).filter((g) => !nextMap.has(g.group_id))
    // Groups re-added: in nextMap and in removeMap
    const addedBack = Array.from(nextMap.values()).filter((g) => removeMap.has(g.group_id))

    // Add newly removed groups to removeGroups if not already present
    let newRemoveGroups = [...removeGroups]
    removed.forEach((g) => {
      if (!removeMap.has(g.group_id)) {
        newRemoveGroups.push(g)
      }
    })

    // Remove groups that were added back from removeGroups
    if (addedBack.length > 0) {
      const addedBackIds = new Set(addedBack.map((g) => g.group_id))
      newRemoveGroups = newRemoveGroups.filter((g) => !addedBackIds.has(g.group_id))
    }

    setSelectedGroups(groups)
    setRemoveGroups(newRemoveGroups)
  }

  const handleRemoveGroup = async () => {
    if (selectedClientIds?.length) {
      try {
        const data = {
          client_ids: selectedClientIds,
          group_ids: selectedGroups.map((group) => group.group_id)
        }

        await dispatch(deleteGuideBulkGroups(data))
        setIsGroupModalVisible(false)
      } catch (error) {
        console.error("Failed to bulk delete client groups:", error)
      }
    }
  }

  const handleCancelGroupAction = () => {
    setIsGroupModalVisible(false)
    setSelectedClient(null)
    setGroupAction("")
    setHideMenuList(false)
    setSelectedClientIds([])
    setSelectedGroups([])
    handleFetchClients()
  }

  return (
    <>
      <div>
        {hasSelected && (
          <BulkActions
            clients={clients}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
            onGroupAction={handleBulkGroupAction}
          />
        )}
        <DataTable
          columns={getHeaders({ field: sortField, order: sortOrder })}
          dataSource={dataSource}
          rowSelection={rowSelectionConfig}
          pagination={false}
          rowClickKey="key"
          onChange={handleTableChange}
          rowKey={(record) => record.key}
        />
      </div>

      <Modal title={title} width={{ xxl: 800, xl: 700 }} open={isVisible} footer={null} onCancel={handleClose} destroyOnClose>
        <ClientContactForm handleClose={handleClose} type={contactType} clients={clients} userIds={userIds} />
      </Modal>

      {isGroupModalVisible && (
        <ClientGroupManage
          groupAction={groupAction}
          isGroupModalVisible={isGroupModalVisible}
          setIsGroupModalVisible={setIsGroupModalVisible}
          selectedClient={selectedClient}
          selectedClientIds={selectedClientIds}
          groups={groups}
          handleSelectedGroupsChange={handleSelectedGroupsChange}
          handleGroupActionClick={handleGroupActionClick}
          hideMenuList={hideMenuList}
          setSelectedClientIds={setSelectedClientIds}
          selectedGroups={selectedGroups}
          handleCancelGroupAction={handleCancelGroupAction}
        />
      )}

      {isCanNotDeleteClientVisible && (
        <CanNotDeleteClient
          open={isCanNotDeleteClientVisible}
          onClose={onCanNotDeleteClientClose}
          handleViewBookingClick={handleViewBookingClick}
        />
      )}
      {isDeleteClientVisible && (
        <DeleteClient
          open={isDeleteClientVisible}
          onClose={onDeleteClientClose}
          handleDeleteClick={handleDeleteClick}
          isClientLoading={isClientLoading}
        />
      )}
    </>
  )
}

ClientTable.propTypes = {
  clients: PropTypes.array.isRequired,
  sortField: PropTypes.string,
  sortOrder: PropTypes.string,
  handleTableChange: PropTypes.func,
  handleFetchClients: PropTypes.func
}

export default ClientTable
